import LiveStatusBadge from "../../Component/UI/LiveStatusBadge";
import { FormEvent, useEffect, useRef, useState } from 'react';
import { SERVER_ADDRESS } from '../../Component/server';
import logo from '../../Image/Logo.png';
import { BRIDGE_CHANNEL, nativeRequest } from './nativeBridge';
import './BroadcastLiveList.css';

type Broadcast = {
  seq: number; live_title: string; live_start: string; live_finish: string;
  live_limit: number; live_status: string; live_img?: string | null; integration_test?: boolean | number;
};
type ListResult = {
  liveList: Broadcast[]; total: number;
  session: { seq: number; mode: '시작' | '리허설' } | null;
  sessionError: boolean;
};
const PAGE_SIZE = 10;
const scheduledTime = (value: string) => {
  const normalized = value.replace(' ', 'T');
  return Date.parse(/[zZ]$|[+-]\d\d:\d\d$/.test(normalized) ? normalized : `${normalized}+09:00`);
};
function thumbnail(value?: string | null): string {
  if (!value?.trim()) return logo;
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith('/')) return `${new URL(SERVER_ADDRESS).origin}${value}`;
  return `${SERVER_ADDRESS}openfile/${value}`;
}

export default function BroadcastLiveList() {
  const appVersion = new URLSearchParams(window.location.search).get('appVersion');
  const version = appVersion && /^\d+\.\d+\.\d+(?:[-+][\w.-]+)?$/.test(appVersion) ? appVersion : null;
  const [draft, setDraft] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [revision, setRevision] = useState(0);
  const [data, setData] = useState<ListResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now);
  const listPending = useRef(false);
  const reload = () => setRevision(value => value + 1);

  useEffect(() => {
    const refresh = (event: MessageEvent) => {
      try {
        const message = JSON.parse(event.data);
        if (message.channel === BRIDGE_CHANNEL && message.type === 'refresh') reload();
      } catch { /* Not an app message. */ }
    };
    window.addEventListener('message', refresh);
    const timer = setInterval(() => setNow(Date.now()), 1000);
    const refreshTimer = setInterval(() => {
      if (document.visibilityState !== 'hidden' && !listPending.current) reload();
    }, 30000);
    return () => { window.removeEventListener('message', refresh); clearInterval(timer); clearInterval(refreshTimer); };
  }, []);

  useEffect(() => {
    let current = true;
    listPending.current = true;
    setLoading(true);
    setError('');
    nativeRequest<ListResult>('list', { currentPage: page, search }).then(result => {
      if (!current) return;
      const lastPage = Math.max(1, Math.ceil(result.total / PAGE_SIZE));
      if (page > lastPage) { setPage(lastPage); return; }
      setData(result);
    }).catch(err => { if (current) setError(err.message); })
      .finally(() => { if (current) { listPending.current = false; setLoading(false); } });
    return () => { current = false; };
  }, [page, search, revision]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    window.scrollTo(0, 0);
    setPage(1);
    setSearch(draft.trim());
    reload();
  };
  const clearSearch = () => { window.scrollTo(0, 0); setDraft(''); setSearch(''); setPage(1); reload(); };
  const action = async (type: 'open' | 'cancel' | 'logout', seq?: number) => {
    if (busy) return;
    setBusy(true);
    setActionError('');
    try {
      const result = await nativeRequest<{ cancelled?: boolean }>(type, seq ? { seq } : {});
      if (type === 'cancel' && !result?.cancelled) reload();
    } catch (err) { setActionError((err as Error).message); }
    finally { setBusy(false); }
  };
  const pages = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE));
  const firstPage = Math.max(1, Math.min(page - 2, pages - 4));
  const pageNumbers = Array.from({ length: Math.min(5, pages) }, (_, index) => firstPage + index);
  const changePage = (value: number) => { setPage(value); window.scrollTo(0, 0); };

  return <main className="broadcast-list">
    <div className="broadcast-list__toolbar">
      <header className="broadcast-list__header">
        <div className="broadcast-list__heading">
          <h1>라이브 목록</h1>
          {version && <span className="broadcast-list__version" aria-label={`앱 버전 ${version}`}>v{version}</span>}
        </div>
        <button type="button" className="broadcast-list__logout" disabled={busy} onClick={() => action('logout')}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M10 5H5v14h5m5-12 5 5-5 5m-6-5h11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
          로그아웃
        </button>
      </header>
      <form className="broadcast-list__search" onSubmit={submit} role="search">
        <div className="search-field">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5" stroke="currentColor" strokeWidth="1.8" /><path d="m16 16 5 5" stroke="currentColor" strokeWidth="1.8" /></svg>
          <input aria-label="라이브 제목 검색" placeholder="라이브 제목을 검색해주세요." value={draft} maxLength={100} onChange={event => setDraft(event.target.value)} enterKeyHint="search" />
          {draft && <button type="button" className="clear" aria-label="검색어 지우기" onClick={clearSearch}>×</button>}
        </div>
        <button className="primary" type="submit">검색</button>
      </form>
      <div className="broadcast-list__summary">
        <span role="status">{loading ? '목록을 불러오는 중…' : error ? '목록 조회 실패' : <>{search ? '검색 결과' : '전체'} <strong>{data?.total ?? 0}</strong>개</>}</span>
        <button type="button" className="quiet" disabled={loading} onClick={reload}>새로고침</button>
      </div>
    </div>
    {actionError && <p className="broadcast-list__error" role="alert">{actionError}</p>}
    {error && data && <p className="broadcast-list__error" role="alert">{error}</p>}
    {error && !data ? <section className="broadcast-list__empty" role="alert"><p>{error}</p><button type="button" onClick={reload}>다시 불러오기</button></section>
      : loading && !data ? <div className="broadcast-list__loading" aria-label="불러오는 중"><span /></div>
      : !data?.liveList.length ? <section className="broadcast-list__empty"><img src={logo} alt="" /><h2>{search ? '검색된 라이브가 없습니다.' : '등록된 라이브가 없습니다.'}</h2><p>{search ? '다른 제목으로 검색해보세요.' : '방송을 예약하면 이곳에서 확인할 수 있습니다.'}</p>{search && <button type="button" onClick={clearSearch}>전체 목록 보기</button>}</section>
      : <>
        {data.sessionError && <p className="broadcast-list__error" role="alert">송출 상태를 확인하지 못했습니다. 새로고침 후 다시 시도해주세요.</p>}
        <ul className="broadcast-list__items">{data.liveList.map(item => {
          const start = scheduledTime(item.live_start);
          const finish = scheduledTime(item.live_finish);
          const active = data.session?.seq === Number(item.seq);
          const anotherActive = !!data.session && !active;
          const disabled = busy || data.sessionError || anotherActive;
          const ongoing = ['리허설', '방송중', '방송 중'].includes(item.live_status);
          const status = active ? data.session?.mode === '리허설' ? '리허설' : '방송중' : item.live_status;
          const integrationTest = item.integration_test === true || item.integration_test === 1;
          const rehearsalAvailable = integrationTest || start - now > 5 * 60 * 1000;
          const liveAvailable = integrationTest || (now >= start - 5 * 60 * 1000 && now < finish);
          const restartable = !integrationTest && item.live_status === '종료' && liveAvailable;
          const hours = (finish - start) / 3600000;
          const image = thumbnail(item.live_img);
          return <li key={item.seq}>
            <div className="broadcast-list__details">
              <div className="broadcast-list__thumbnail"><img src={image} className={image === logo ? 'fallback' : ''} alt={image === logo ? 'Fanbird 로고' : `${item.live_title} 썸네일`} loading="lazy" onError={event => { event.currentTarget.onerror = null; event.currentTarget.src = logo; event.currentTarget.className = 'fallback'; event.currentTarget.alt = 'Fanbird 로고'; }} /></div>
              <div className="broadcast-list__info">
                <LiveStatusBadge status={status} className="broadcast-list__status" />
                <h2>{item.live_title}</h2>
                <p className="date">{item.live_start.slice(0, 16).replace('T', ' ').replace(/-/g, '.')}</p>
                <p className="meta">{Number.isFinite(hours) ? `${hours}시간` : ''}<span>수용 인원 {item.live_limit}명</span></p>
              </div>
            </div>
            <div className="broadcast-list__actions">
              {!active && (item.live_status === '예약' || (integrationTest && item.live_status === '종료')) && <>
                {item.live_status === '예약' && <button type="button" className="cancel" disabled={disabled} onClick={() => action('cancel', item.seq)}>예약 취소</button>}
                <button type="button" className="rehearsal-button" disabled={disabled || !rehearsalAvailable} onClick={() => action('open', item.seq)}>리허설</button>
                <button type="button" className="primary" disabled={disabled || !liveAvailable} onClick={() => action('open', item.seq)}>라이브 송출</button>
              </>}
              {(active || ongoing) && <button type="button" className="primary" disabled={disabled} onClick={() => action('open', item.seq)}>{active ? status === '리허설' ? '리허설로 돌아가기' : '방송으로 돌아가기' : item.live_status === '리허설' ? '리허설 다시 연결' : '방송 화면으로'}</button>}
              {!active && !ongoing && restartable && <button type="button" className="primary" disabled={disabled} onClick={() => action('open', item.seq)}>다시 방송하기</button>}
            </div>
          </li>;
        })}</ul>
        {pages > 1 && <nav className="broadcast-list__pagination" aria-label="라이브 목록 페이지">
          <button type="button" aria-label="이전 페이지" disabled={page === 1} onClick={() => changePage(page - 1)}>‹</button>
          {pageNumbers.map(value => <button type="button" key={value} aria-label={`${value}페이지`} aria-current={value === page ? 'page' : undefined} onClick={() => changePage(value)}>{value}</button>)}
          <button type="button" aria-label="다음 페이지" disabled={page === pages} onClick={() => changePage(page + 1)}>›</button>
        </nav>}
        <p className="broadcast-list__page-label">{page} / {pages} 페이지</p>
      </>}
  </main>;
}
